/**
 * MaterialsTab Component
 * Membrane run calculator, materials summary with pricing, and extras
 */

import React, { useMemo, useState } from 'react';
import { Card, Button, EditableCell } from '../ui/FormElements';
import { formatCurrency, getLapWidth, computeMembraneRuns, calculateTotalArea, generateId } from '../../utils/calculations';
import { OVERHANG_OPTIONS, DEFAULT_WASTAGE } from '../../constants/flatCalc';

const MaterialsTab = ({ areas, spec, materials, onChange, products, runCalcData, onRunCalcChange, onNext }) => {
  const totalArea = calculateTotalArea(areas.areas);
  const totalUpstandLength = areas.upstands.reduce((sum, u) => sum + (u.length || 0), 0);
  
  // Get all selected products from spec
  const getProduct = (id) => id ? products.find(p => p.id === parseInt(id)) : null;
  
  const deckMembrane = getProduct(spec.deckMembraneId);
  const flashingMembrane = getProduct(spec.flashingMembraneId);
  const detailMembrane = getProduct(spec.detailMembraneId);
  const deckUnderlay = getProduct(spec.deckUnderlayId);
  const deckCap = getProduct(spec.deckCapId);
  const flashingUnderlay = getProduct(spec.flashingUnderlayId);
  const flashingCap = getProduct(spec.flashingCapId);
  const deckAdhesive = getProduct(spec.deckAdhesiveId);
  const detailAdhesive = getProduct(spec.detailAdhesiveId);
  const vcl = getProduct(spec.vclId);
  const vclPrimer = getProduct(spec.vclPrimerId);
  const deckPrimer = getProduct(spec.deckPrimerId);
  const insulation = getProduct(spec.insulationId);
  const insulationAdhesive = getProduct(spec.insulationAdhesiveId);

  // Build list of membrane products for run calculations
  const membraneProducts = useMemo(() => {
    const list = [];
    if (deckMembrane) list.push({ ...deckMembrane, label: deckMembrane.name, key: 'deck_membrane' });
    if (deckUnderlay) list.push({ ...deckUnderlay, label: `${deckUnderlay.name} (Underlay)`, key: 'deck_underlay' });
    if (deckCap) list.push({ ...deckCap, label: `${deckCap.name} (Cap)`, key: 'deck_cap' });
    return list;
  }, [deckMembrane, deckUnderlay, deckCap]);

  // Lap overrides
  const getLapOverride = (productId) => runCalcData.lapOverrides?.[productId];
  const setLapOverride = (productId, value) => {
    onRunCalcChange({ 
      ...runCalcData, 
      lapOverrides: { ...runCalcData.lapOverrides, [productId]: value } 
    });
  };

  const toggleInvert = (areaId) => {
    onRunCalcChange({ 
      ...runCalcData, 
      invertedAreas: { ...runCalcData.invertedAreas, [areaId]: !runCalcData.invertedAreas?.[areaId] } 
    });
  };

  // Calculate membrane runs
  const membraneCalcs = useMemo(() => {
    return membraneProducts.map(product => {
      const lapWidth = getLapOverride(product.id) ?? getLapWidth(product);
      const rollWidth = product.width_mm || 1000;
      const rollLength = product.length_m || 10;
      const overhang = runCalcData.overhang ?? 100;
      const overhangSides = runCalcData.overhangSides ?? 'both';
      
      const rollSpec = { rollWidth, lapWidth, rollLength, overhang, overhangSides };
      
      let totalLinM = 0;
      let totalLapLength = 0;
      
      areas.areas.forEach(area => {
        const inverted = runCalcData.invertedAreas?.[area.id] || false;
        const result = computeMembraneRuns(rollSpec, area.length || 0, area.width || 0, inverted);
        totalLinM += result.totalLinM;
        totalLapLength += result.totalLapLengthM;
      });
      
      const totalRolls = rollLength > 0 ? totalLinM / rollLength : 0;
      
      return {
        product,
        rollSpec,
        totalLinM,
        totalLapLength,
        totalRolls,
        effectiveCover: rollWidth - lapWidth
      };
    });
  }, [membraneProducts, areas.areas, runCalcData]);

  // Area runs for display
  const primaryMembrane = membraneProducts[0];
  const areaRuns = useMemo(() => {
    if (!primaryMembrane) return [];
    const lapWidth = getLapOverride(primaryMembrane.id) ?? getLapWidth(primaryMembrane);
    const rollSpec = {
      rollWidth: primaryMembrane.width_mm || 1000,
      lapWidth,
      rollLength: primaryMembrane.length_m || 10,
      overhang: runCalcData.overhang ?? 100,
      overhangSides: runCalcData.overhangSides ?? 'both'
    };
    return areas.areas.map(area => {
      const inverted = runCalcData.invertedAreas?.[area.id] || false;
      return { ...area, inverted, result: computeMembraneRuns(rollSpec, area.length || 0, area.width || 0, inverted) };
    });
  }, [primaryMembrane, areas.areas, runCalcData]);

  const runTotals = useMemo(() => areaRuns.reduce((acc, a) => ({
    totalLinM: acc.totalLinM + a.result.totalLinM,
    totalRolls: acc.totalRolls + a.result.totalRolls,
    totalLapLength: acc.totalLapLength + a.result.totalLapLengthM,
    totalPerimeter: acc.totalPerimeter + a.result.perimeterM
  }), { totalLinM: 0, totalRolls: 0, totalLapLength: 0, totalPerimeter: 0 }), [areaRuns]);

  // Build complete materials list
  const materialsList = useMemo(() => {
    const list = [];
    
    // Helper to add material
    const addMaterial = (product, calculated, unit, category, wastageKey = 'membrane') => {
      if (!product) return;
      const existing = materials.find(m => m.productId === product.id);
      list.push({
        id: product.id,
        productId: product.id,
        name: product.name,
        calculated: calculated,
        wastage: existing?.wastage ?? DEFAULT_WASTAGE[wastageKey] ?? 10,
        price: existing?.price ?? (product.rrp_per_unit || 0),
        unit,
        category
      });
    };

    // Deck membranes (from run calculator)
    membraneCalcs.forEach(calc => {
      const existing = materials.find(m => m.productId === calc.product.id);
      list.push({
        id: calc.product.id,
        productId: calc.product.id,
        name: calc.product.label,
        calculated: calc.totalRolls,
        wastage: existing?.wastage ?? DEFAULT_WASTAGE.membrane,
        price: existing?.price ?? (calc.product.rrp_per_unit || 0),
        unit: 'rolls',
        category: 'Membrane',
        showDecimal: true // Show 0.6 rolls not 1 roll
      });
    });

    // Flashing membrane (based on upstand length)
    if (flashingMembrane && totalUpstandLength > 0) {
      const rollLength = flashingMembrane.length_m || 10;
      const calculated = totalUpstandLength / rollLength;
      addMaterial(flashingMembrane, calculated, 'rolls', 'Membrane');
    }

    // Detail membrane (based on details count - rough estimate)
    if (detailMembrane) {
      const detailCount = (areas.details.outlets || 0) + 
                         (areas.details.penetrationsSmall || 0) + 
                         (areas.details.penetrationsLarge || 0) + 
                         (areas.details.rooflights || 0) +
                         (areas.details.internalAngles || 0) +
                         (areas.details.externalAngles || 0);
      if (detailCount > 0) {
        // Estimate ~0.5m per detail
        const rollLength = detailMembrane.length_m || 10;
        const calculated = (detailCount * 0.5) / rollLength;
        addMaterial(detailMembrane, calculated, 'rolls', 'Membrane');
      }
    }

    // Deck adhesive (for adhered membranes)
    if (deckAdhesive && spec.membraneFixingMethod === 'adhered') {
      const coverage = deckAdhesive.coverage_m2 || 3; // m² per unit
      let calcArea = totalArea;
      // If using detail adhesive as deck (2x coverage)
      if (spec.useDetailAsDeckAdhesive && detailAdhesive) {
        // Skip - will be added with detail adhesive at 2x
      } else {
        const calculated = calcArea / coverage;
        addMaterial(deckAdhesive, calculated, 'units', 'Adhesive', 'adhesive');
      }
    }

    // Detail/contact adhesive
    if (detailAdhesive) {
      let coverage = detailAdhesive.coverage_m2 || 6; // m² per unit
      let calcArea = totalUpstandLength * 0.6; // Estimate 600mm girth average
      
      // Add detail areas
      const detailArea = ((areas.details.outlets || 0) * 0.25) + 
                        ((areas.details.penetrationsSmall || 0) * 0.15) +
                        ((areas.details.penetrationsLarge || 0) * 0.35) +
                        ((areas.details.rooflights || 0) * 1.5);
      calcArea += detailArea;
      
      // If also used as deck adhesive (2x coverage = half the coverage rate)
      if (spec.useDetailAsDeckAdhesive) {
        calcArea += totalArea;
        coverage = coverage / 2; // 2x coverage means half the spread rate
      }
      
      const calculated = calcArea / coverage;
      addMaterial(detailAdhesive, calculated, 'units', 'Adhesive', 'adhesive');
    }

    // VCL
    if (vcl && !spec.useExistingAsVcl) {
      const coverage = vcl.coverage_m2 || 27;
      const calculated = totalArea / coverage;
      addMaterial(vcl, calculated, 'rolls', 'VCL', 'vcl');
    }

    // VCL Primer
    if (vclPrimer && vcl && !spec.useExistingAsVcl) {
      const coverage = vclPrimer.coverage_m2 || 8;
      const calculated = totalArea / coverage;
      addMaterial(vclPrimer, calculated, 'units', 'Primer');
    }

    // Deck Primer (RBM)
    if (deckPrimer) {
      const coverage = deckPrimer.coverage_m2 || 8;
      const calculated = totalArea / coverage;
      addMaterial(deckPrimer, calculated, 'units', 'Primer');
    }

    // Insulation
    if (insulation) {
      const coverage = insulation.coverage_m2 || 2.88;
      const calculated = (totalArea / coverage) * (spec.insulationLayers || 1);
      addMaterial(insulation, calculated, 'boards', 'Insulation', 'insulation');
    }

    // Insulation adhesive
    if (insulationAdhesive && spec.insulationFixingMethod === 'adhered') {
      const coverage = insulationAdhesive.coverage_m2 || 4;
      const bondingArea = spec.insulationBondingArea || totalArea;
      const calculated = (bondingArea * (spec.insulationLayers || 1)) / coverage;
      addMaterial(insulationAdhesive, calculated, 'units', 'Adhesive', 'adhesive');
    }

    return list;
  }, [
    membraneCalcs, flashingMembrane, detailMembrane, deckAdhesive, detailAdhesive,
    vcl, vclPrimer, deckPrimer, insulation, insulationAdhesive,
    totalArea, totalUpstandLength, areas.details, spec, materials
  ]);

  // Extras (manually added items)
  const extras = materials.filter(m => m.isExtra) || [];

  const addExtra = () => {
    const newExtra = {
      id: generateId(),
      productId: generateId(),
      name: '',
      calculated: 1,
      wastage: 0,
      price: 0,
      unit: 'nr',
      isExtra: true
    };
    onChange([...materials, newExtra]);
  };

  const updateExtra = (id, field, value) => {
    onChange(materials.map(m => m.id === id ? { ...m, [field]: value } : m));
  };

  const removeExtra = (id) => {
    onChange(materials.filter(m => m.id !== id));
  };

  const updateMaterial = (productId, field, value) => {
    const existing = materials.find(m => m.productId === productId);
    if (existing) {
      onChange(materials.map(m => m.productId === productId ? { ...m, [field]: value } : m));
    } else {
      const item = materialsList.find(m => m.productId === productId);
      if (item) {
        onChange([...materials, { ...item, [field]: value }]);
      }
    }
  };

  // Calculate totals - use decimal for display, ceiling only for ordering
  const materialsTotal = useMemo(() => {
    let total = 0;
    materialsList.forEach(m => {
      const withWastage = m.calculated * (1 + m.wastage / 100);
      // Round to 0.1 for membrane rolls, ceiling for everything else
      const required = m.showDecimal 
        ? Math.ceil(withWastage * 10) / 10 
        : Math.ceil(withWastage);
      total += required * m.price;
    });
    extras.forEach(e => {
      total += (e.calculated || 0) * (e.price || 0);
    });
    return total;
  }, [materialsList, extras]);

  return (
    <div>
      {/* Membrane Run Calculator */}
      <Card title="Membrane Run Calculator">
        {membraneProducts.length === 0 ? (
          <p className="text-slate-500 text-center py-4">
            Select a membrane in Spec tab to calculate runs.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-700">
                    <th className="text-left py-2 px-2">Product</th>
                    <th className="text-right py-2 px-2">Width (mm)</th>
                    <th className="text-right py-2 px-2">Lap (mm)</th>
                    <th className="text-right py-2 px-2">Eff. Cover</th>
                    <th className="text-right py-2 px-2">Roll (m)</th>
                    <th className="text-right py-2 px-2">Lin.m</th>
                    <th className="text-right py-2 px-2">Rolls</th>
                  </tr>
                </thead>
                <tbody>
                  {membraneCalcs.map(calc => (
                    <tr key={calc.product.id} className="border-b border-slate-700/50">
                      <td className="py-2 px-2 text-white">{calc.product.label}</td>
                      <td className="py-2 px-2 text-right text-slate-300">{calc.rollSpec.rollWidth}</td>
                      <td className="py-1 px-2 w-24 bg-amber-500/10">
                        <EditableCell 
                          value={calc.rollSpec.lapWidth} 
                          onChange={(v) => setLapOverride(calc.product.id, v)} 
                          highlight 
                        />
                      </td>
                      <td className="py-2 px-2 text-right text-slate-300">{calc.effectiveCover}</td>
                      <td className="py-2 px-2 text-right text-slate-300">{calc.rollSpec.rollLength}</td>
                      <td className="py-2 px-2 text-right text-white">{calc.totalLinM.toFixed(2)}</td>
                      <td className="py-2 px-2 text-right text-amber-500 font-semibold">
                        {calc.totalRolls.toFixed(1)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Overhang settings */}
            <div className="flex items-center gap-4 py-2">
              <span className="text-slate-400 text-sm">Overhang:</span>
              <div className="w-20 bg-amber-500/10 rounded">
                <EditableCell
                  value={runCalcData.overhang ?? 100}
                  onChange={(v) => onRunCalcChange({ ...runCalcData, overhang: v })}
                  highlight
                />
              </div>
              <span className="text-slate-500 text-sm">mm</span>
              <select
                value={runCalcData.overhangSides ?? 'both'}
                onChange={(e) => onRunCalcChange({ ...runCalcData, overhangSides: e.target.value })}
                className="bg-slate-700 text-white text-sm rounded px-2 py-1 ml-2"
              >
                {OVERHANG_OPTIONS.map(o => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Area breakdown */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-400 border-b border-slate-700">
                    <th className="text-left py-2 px-2">Area</th>
                    <th className="text-right py-2 px-2">L (m)</th>
                    <th className="text-right py-2 px-2">W (m)</th>
                    <th className="text-center py-2 px-2">Invert?</th>
                    <th className="text-right py-2 px-2">Runs</th>
                    <th className="text-right py-2 px-2">Run Len</th>
                    <th className="text-right py-2 px-2">Lin.m</th>
                  </tr>
                </thead>
                <tbody>
                  {areaRuns.map(a => (
                    <tr key={a.id} className="border-b border-slate-700/50">
                      <td className="py-1 px-2 text-white">{a.name || 'Unnamed'}</td>
                      <td className="py-1 px-2 text-right text-slate-300">{(a.length || 0).toFixed(2)}</td>
                      <td className="py-1 px-2 text-right text-slate-300">{(a.width || 0).toFixed(2)}</td>
                      <td className="py-1 px-2 text-center">
                        <input
                          type="checkbox"
                          checked={a.inverted}
                          onChange={() => toggleInvert(a.id)}
                          className="rounded border-slate-600 bg-slate-700 text-amber-500"
                        />
                      </td>
                      <td className="py-1 px-2 text-right text-white">{a.result.numRuns}</td>
                      <td className="py-1 px-2 text-right text-white">{a.result.runLengthM.toFixed(2)}</td>
                      <td className="py-1 px-2 text-right text-white">{a.result.totalLinM.toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-700/30 font-semibold">
                    <td colSpan="6" className="py-2 px-2 text-white">Total Linear Metres</td>
                    <td className="py-2 px-2 text-right text-white">{runTotals.totalLinM.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-700">
              <div className="bg-slate-900/50 rounded-lg px-4 py-3">
                <p className="text-slate-400 text-xs">Total Lap Length</p>
                <p className="text-white font-semibold">{runTotals.totalLapLength.toFixed(2)} m</p>
              </div>
              <div className="bg-slate-900/50 rounded-lg px-4 py-3">
                <p className="text-slate-400 text-xs">Total Perimeter</p>
                <p className="text-white font-semibold">{runTotals.totalPerimeter.toFixed(2)} m</p>
              </div>
              <div className="bg-slate-900/50 rounded-lg px-4 py-3">
                <p className="text-slate-400 text-xs">Linear Metres</p>
                <p className="text-white font-semibold">{runTotals.totalLinM.toFixed(2)} m</p>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Materials Summary */}
      <Card title="Materials Summary">
        {materialsList.length === 0 ? (
          <p className="text-slate-500 text-center py-4">Select products in Spec tab.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-400 border-b border-slate-700">
                  <th className="text-left py-2 px-2">Product</th>
                  <th className="text-left py-2 px-2">Category</th>
                  <th className="text-right py-2 px-2">Calculated</th>
                  <th className="text-right py-2 px-2">Wastage</th>
                  <th className="text-right py-2 px-2">Required</th>
                  <th className="text-right py-2 px-2">Unit Price</th>
                  <th className="text-right py-2 px-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {materialsList.map(m => {
                  const withWastage = m.calculated * (1 + m.wastage / 100);
                  // Show decimal for membranes, round up for others
                  const required = m.showDecimal 
                    ? Math.ceil(withWastage * 10) / 10 
                    : Math.ceil(withWastage);
                  return (
                    <tr key={m.id} className="border-b border-slate-700/50">
                      <td className="py-2 px-2 text-white">{m.name}</td>
                      <td className="py-2 px-2 text-slate-400 text-xs">{m.category}</td>
                      <td className="py-2 px-2 text-right text-slate-300">
                        {m.calculated.toFixed(2)} {m.unit}
                      </td>
                      <td className="py-1 px-2 bg-amber-500/10 w-24">
                        <EditableCell
                          value={m.wastage}
                          onChange={(v) => updateMaterial(m.productId, 'wastage', v)}
                          suffix="%"
                          highlight
                        />
                      </td>
                      <td className="py-2 px-2 text-right text-white font-medium">
                        {m.showDecimal ? required.toFixed(1) : required} {m.unit}
                      </td>
                      <td className="py-1 px-2 bg-amber-500/10 w-28">
                        <EditableCell
                          value={m.price}
                          onChange={(v) => updateMaterial(m.productId, 'price', v)}
                          prefix="£"
                          highlight
                        />
                      </td>
                      <td className="py-2 px-2 text-right text-amber-500 font-semibold">
                        {formatCurrency(required * m.price)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Extras */}
      <Card 
        title="Extras" 
        action={<Button size="sm" onClick={addExtra}>+ Add Item</Button>}
      >
        {extras.length === 0 ? (
          <p className="text-slate-500 text-center py-4">
            Add items not listed above (trims, fixings, etc.)
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-400 border-b border-slate-700">
                  <th className="text-left py-2 px-2">Description</th>
                  <th className="text-right py-2 px-2 w-20">Qty</th>
                  <th className="text-right py-2 px-2 w-20">Unit</th>
                  <th className="text-right py-2 px-2 w-28">Unit Price</th>
                  <th className="text-right py-2 px-2">Total</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {extras.map(e => (
                  <tr key={e.id} className="border-b border-slate-700/50">
                    <td className="py-1 px-2">
                      <input
                        type="text"
                        value={e.name || ''}
                        placeholder="e.g. Edge trim 3m"
                        onChange={(ev) => updateExtra(e.id, 'name', ev.target.value)}
                        className="w-full bg-transparent text-white focus:outline-none"
                      />
                    </td>
                    <td className="py-1 px-2 bg-amber-500/10">
                      <EditableCell
                        value={e.calculated || 0}
                        onChange={(v) => updateExtra(e.id, 'calculated', v)}
                        highlight
                      />
                    </td>
                    <td className="py-1 px-2">
                      <select
                        value={e.unit || 'nr'}
                        onChange={(ev) => updateExtra(e.id, 'unit', ev.target.value)}
                        className="w-full bg-slate-700 border-0 text-white text-sm rounded px-2 py-1"
                      >
                        <option value="nr">nr</option>
                        <option value="m">m</option>
                        <option value="m²">m²</option>
                        <option value="rolls">rolls</option>
                        <option value="boxes">boxes</option>
                        <option value="packs">packs</option>
                      </select>
                    </td>
                    <td className="py-1 px-2 bg-amber-500/10">
                      <EditableCell
                        value={e.price || 0}
                        onChange={(v) => updateExtra(e.id, 'price', v)}
                        prefix="£"
                        highlight
                      />
                    </td>
                    <td className="py-2 px-2 text-right text-amber-500 font-semibold">
                      {formatCurrency((e.calculated || 0) * (e.price || 0))}
                    </td>
                    <td className="py-1 px-2">
                      <button
                        onClick={() => removeExtra(e.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Grand Total */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4 mb-6">
        <div className="flex justify-between items-center">
          <span className="text-white font-semibold text-lg">Materials Total</span>
          <span className="text-amber-500 font-bold text-xl">{formatCurrency(materialsTotal)}</span>
        </div>
      </div>
      
      {/* Navigation */}
      <div className="flex justify-end mt-6">
        <Button onClick={onNext}>Next: Labour →</Button>
      </div>
    </div>
  );
};

export default MaterialsTab;
